// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, userAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    const accessToken = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');

    if (accessToken && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setToken(accessToken);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Erro ao parse dos dados do usuário:', error);
        logout();
      }
    }
    setLoading(false);
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      console.log('Tentando fazer login com:', credentials.email || credentials.username);

      const response = await authAPI.login(credentials);
      console.log('Resposta do login:', response.data);

      const { access, refresh } = response.data;

      // Salvar tokens
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      // Buscar perfil completo do usuário (incluindo is_staff)
      try {
        const profileResponse = await userAPI.profile();
        const userData = profileResponse.data;
        console.log('Perfil do usuário:', userData);

        localStorage.setItem('user_data', JSON.stringify(userData));
        setUser(userData);
      } catch (profileError) {
        console.error('Erro ao buscar perfil:', profileError);
        // Fallback para dados básicos
        const userData = {
          email: credentials.email || credentials.username,
          username: credentials.email || credentials.username,
          is_staff: false,
        };
        localStorage.setItem('user_data', JSON.stringify(userData));
        setUser(userData);
      }

      setToken(access);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Erro no login:', error);
      console.error('Resposta do erro:', error.response);

      let errorMessage = 'Erro ao fazer login';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.non_field_errors) {
        errorMessage = error.response.data.non_field_errors[0];
      }

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);

      // Após registrar, fazer login automaticamente usando email
      const loginResult = await login({
        email: userData.email,
        password: userData.password
      });

      return loginResult;
    } catch (error) {
      console.error('Erro no registro:', error);
      return {
        success: false,
        error: error.response?.data || 'Erro ao registrar usuário'
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user_data', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};